// Wspolna detekcja locale dla SSR. Uzywana w dwoch miejscach:
//  - src/server.ts (Express, do logow/diagnostyki)
//  - src/app/app.server.module.ts (server-side APP_INITIALIZER, ktory faktycznie
//    przelacza ngx-translate PRZED pierwszym renderem, czytajac framework token REQUEST).
//
// Wazne: @angular/ssr v21 NIE wstrzykuje per-request providerow przez 2. argument
// angularApp.handle(req, requestContext). Drugi argument to opaque `requestContext`,
// wiec dawny REQUEST_LOCALE nigdy nie trafial do DI (initial render zawsze 'pl').
// Engine providuje natomiast framework token REQUEST (@angular/core) dla RenderMode.Server,
// dlatego locale liczymy wewnatrz Angulara z REQUEST.url.
//
// 🔴 FIX 2b: jedynym sygnalem jezyka jest ADRES (prefiks sciezki, potem ?lang=).
// Accept-Language NIE jest juz czytany — szczegoly i uzasadnienie przy detectLocaleFrom().

// Jezyki wspierane przez aplikacje (zgodne z TranslationsService.addLangs
// i plikami src/assets/i18n/*.json). Whitelist chroni przed renderem nieistniejacego
// locale; cokolwiek spoza listy -> fallback 'pl'.
export const SUPPORTED_LOCALES = ['pl', 'en'] as const;
export const DEFAULT_LOCALE = 'pl';

/**
 * Normalizuje dowolna wartosc do wspieranego locale albo null.
 * Eksportowana, zeby klient (TranslationsService, T7) czytal `?lang=` TYM SAMYM
 * parserem co SSR — `null` (a nie 'pl') dla smiecia jest tu istotne: pozwala
 * spasc do nastepnego zrodla zamiast zablokowac je fallbackiem.
 */
export function normalizeLocale(v: unknown): 'pl' | 'en' | null {
  if (typeof v !== 'string') return null;
  // bierzemy tylko primary subtag (np. 'en-US' -> 'en'), lowercase
  const tag = v.trim().toLowerCase().split('-')[0];
  return (SUPPORTED_LOCALES as readonly string[]).includes(tag)
    ? (tag as 'pl' | 'en')
    : null;
}

function norm(v: unknown): string | null {
  return normalizeLocale(v);
}

/** Pathname z pelnego URL-a; nigdy nie rzuca (build-time extraction podaje dziwne wejscia). */
function safePathname(url: string): string {
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
}

/**
 * JEDNO zrodlo prawdy dla parsowania prefiksu locale ze sciezki (DESIGN §2.4).
 * Importuja to: LocaleUrlSerializer (§2.1) oraz normalizator Express (§2.2, T12).
 * Trzy niezalezne implementacje tego samego parsowania to gwarantowany rozjazd.
 *
 * Zachlanne: zjada KAZDY wiodacy segment locale, ale decyduje PIERWSZY
 * (`/pl/pl/menu` -> locale 'pl', rest '/menu'; `/en/pl/menu` -> locale 'en', rest '/menu').
 *
 * `prefix` to prefiks ADRESU: '' dla polskiego (H6 — PL nie ma prefiksu NIGDY), 'en' dla angielskiego.
 * `locale` to WYKRYTY jezyk ('pl' | 'en' | null gdy sciezka nic nie mowi) — te dwie rzeczy
 * nie sa tym samym i mylenie ich jest zrodlem bledu `/pl/` w canonicalu.
 *
 * Wejscie moze byc samym pathname ('/en/menu') albo stringiem z query/fragmentem
 * ('/en/menu?c=abc#x') — czesc po pierwszym '?' lub '#' jest przenoszona do `rest` bez zmian,
 * zeby serializer mogl uzyc tej samej funkcji na surowym URL-u z routera.
 */
export function splitLocalePrefix(pathname: string | undefined | null): {
  prefix: '' | 'en';
  locale: 'pl' | 'en' | null;
  rest: string;
} {
  const raw = typeof pathname === 'string' ? pathname : '';
  const cut = ((): number => {
    const q = raw.indexOf('?');
    const h = raw.indexOf('#');
    if (q === -1) return h;
    if (h === -1) return q;
    return Math.min(q, h);
  })();
  const path = cut === -1 ? raw : raw.slice(0, cut);
  const suffix = cut === -1 ? '' : raw.slice(cut);

  const segs = path.split('/').filter((s) => s.length > 0);
  let decided: 'pl' | 'en' | null = null;
  // WIELKOSC LITER: porownanie jest CELOWO case-sensitive (inaczej niz norm()/normalizeLocale(),
  // ktore lowercase'uje wartosci naglowkow i ?lang=). Prefiks jest czescia ADRESU, a adresy
  // trzymamy w jednej, kanonicznej postaci '/en/...'.
  // Zmierzone na zbudowanym artefakcie: '/EN/menu' -> 302 /404 ZAROWNO na develop (3227dc5),
  // JAK I na tej galezi — czyli to nie jest regresja prefiksow, tylko zachowanie kazdej
  // nieznanej sciezki. Co wazniejsze, zlagodzenie porownania TUTAJ bylo by zludzeniem ochrony:
  // '/EN/**' nie pasuje do wpisu manifestu '/en/**', wiec @angular/ssr odrzuca zadanie
  // PRZED bootstrapem i ten kod nigdy sie nie wykonuje. Kanonizacja wielkosci liter nalezy
  // do normalizatora Express (T12, §2.2), gdzie 301 da sie faktycznie wystawic.
  while (segs.length > 0 && (SUPPORTED_LOCALES as readonly string[]).includes(segs[0])) {
    decided = decided ?? (segs[0] as 'pl' | 'en');
    segs.shift();
  }
  const rest = '/' + segs.join('/');
  return {
    prefix: decided === 'en' ? 'en' : '',
    locale: decided,
    rest: rest + suffix,
  };
}

/**
 * 🔴 ADRES JEST JEDYNYM SYGNALEM JEZYKA (FIX 2b, decyzja produktowa).
 *
 * Regula: "bez okreslenia pl albo en w url domyslnie jest polski". Bezprefiksowy
 * adres renderuje sie po POLSKU dla KAZDEGO, bezwarunkowo — Accept-Language NIE
 * jest juz czlonem lancucha serwerowego.
 *
 * Dlaczego naglowek wypadl (a nie: "bo tak wygodniej"):
 *  - URL to JEDYNY sygnal locale, ktory niesie ze soba crawler, cache i udostepniony link.
 *    Accept-Language potrafil zmienic CIALO '/menu', ale strona nie jest cache'owalna
 *    per-URL, wiec ten sam adres oddawal raz PL raz EN. Google dostawal angielskie
 *    '/menu' sprzeczne z wlasnym canonicalem tej strony.
 *  - Przegladarki anglojezyczne trafia do drzewa EN pozniej, normalizatorem Express z T12,
 *    przez **301** — widoczne, cache'owalne i odwracalne, inaczej niz niewidzialny
 *    branch po naglowku.
 *  - Efekt uboczny, ktory byl celem sam w sobie: lancuch SERWERA i lancuch PRZEGLADARKI
 *    przestaja sie rozjezdzac na naglowku (patrz locale-chain-divergence.spec.ts).
 *
 * Priorytet po zmianie: ?lang= (jawny wybor / link per-locale) -> fallback 'pl'.
 * Wynik zawsze nalezy do SUPPORTED_LOCALES.
 *
 * @param langQuery  surowa wartosc query param `lang` (string | string[] | undefined)
 * @param _acceptLanguage  surowy naglowek Accept-Language — CELOWO IGNOROWANY.
 *   Parametr zostaje w sygnaturze swiadomie: wolajacy (app.server.module.ts) dalej go
 *   podaje, a testy moga podac naglowek i udowodnic, ze NIE ma on wplywu. Usuniecie
 *   parametru zamienilo by ten dowod w cisze.
 */
export function detectLocaleFrom(
  langQuery: unknown,
  // Podkreslnik = konwencja "celowo nieuzywany". Parametr zostaje dla wolajacych
  // i dla testow, ktore podaja naglowek, by udowodnic jego brak wplywu.
  _acceptLanguage?: string | undefined | null,
): string {
  // 1) ?lang= query (Express moze zwrocic string|string[])
  const fromQuery = norm(Array.isArray(langQuery) ? langQuery[0] : langQuery);
  if (fromQuery) return fromQuery;

  // 2) fallback — bez prefiksu i bez ?lang= zawsze polski (patrz komentarz wyzej).
  return DEFAULT_LOCALE;
}

/**
 * Wariant dla framework tokenu REQUEST (@angular/core) — parsuje ?lang= z REQUEST.url.
 * REQUEST.url bywa sciezka wzgledna ('/path?lang=en') lub pelnym URL — obslugujemy oba.
 *
 * DESIGN §2.4, wariant B + FIX 2b: priorytet SCIEZKOWY, BEZ naglowka —
 *   prefiks sciezki -> ?lang= -> 'pl'.
 * ?lang= zostaje jako obrona na wypadek wylaczonego normalizatora (T12) i do testow
 * na dv1. Accept-Language wypadl swiadomie — uzasadnienie przy detectLocaleFrom().
 * Sygnatura sie NIE zmienia: naglowek dalej wolno podac, po prostu nic nie robi.
 */
export function detectLocaleFromRequestLike(
  url: string | undefined | null,
  acceptLanguage?: string | undefined | null,
): string {
  // 0) PRIORYTET SCIEZKOWY — sciezka bije query.
  if (typeof url === 'string' && url.length > 0) {
    const pathOnly = url.startsWith('http') ? safePathname(url) : url;
    const fromPath = splitLocalePrefix(pathOnly).locale;
    if (fromPath) return fromPath;
  }

  let langQuery: string | null = null;
  if (typeof url === 'string' && url.length > 0) {
    const qIndex = url.indexOf('?');
    if (qIndex !== -1) {
      const params = new URLSearchParams(url.slice(qIndex + 1));
      langQuery = params.get('lang');
    }
  }
  return detectLocaleFrom(langQuery, acceptLanguage);
}
